import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges,
    inject
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { ImageModule } from '@df/module-image/image.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';

import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { DfUiModule } from '@df/ui/df-ui.module';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { PhotoswipeLightboxComponent } from '@df/ui/photoswipe/photoswipe-lightbox.component';
import { PhotoswipeDirective } from '@df/ui/photoswipe/photoswipe.directive';
import { PhotoswipeUiDefaultOptions } from '@df/ui/photoswipe/photoswipe.types';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import SwiperCore, { FreeMode, Pagination, Swiper } from 'swiper';
import { SwiperModule } from 'swiper/angular';
import { SwiperEvents } from 'swiper/types';
import { LocalGtmService } from '../../../module/gtm/gtm.service.decorator';
import { ScrollNavTargetDirective } from '../../../ui/scroll-nav/scroll-nav-target.directive';
import { ShopThisLookComponent } from '../../../ui/shop-this-look/shop-this-look.component';
import { SwiperDotsComponent } from '../../../ui/widget/swiper-dots/swiper-dots.component';
import { Viewer3dService } from '../../model/product/3dviewer.service';
import { LocalProduct } from '../../model/product/product.decorator';
import { FeaturesSectionComponent } from './features-section/features-section.component';
import { productHighlightsLabel } from './product-highlights/product-highlights.component';

SwiperCore.use([FreeMode, Pagination]);
export type IProductGalleryData = Catalog.IBlockData<IProductGalleryContent>;
export interface IProductGalleryContent {
    galleryRows?: {
        layout: EMediaRowLayout;
    }[];
}
enum EMediaRowLayout {
    portraitsPair,
    landscape,
    portrait
}
export interface IMediaRowItem extends Nexus.Lib.Catalog.Type.IProductMediaItem {
    ratio: number;
    classes?: string;
    position: number;
    uid: string;
    isLandscape?: boolean;
    shopTheLook?: string[] | undefined;
    isComponentContainer?: boolean;
    iframeSrc?: string;
    iframeSrcSafe?: SafeResourceUrl;
}
@Component({
    selector: 'product-gallery',
    templateUrl: './product-gallery.html',
    styleUrls: ['./product-gallery.scss'],
    standalone: true,
    imports: [
        ScrollNavTargetDirective,
        TranslationModule,
        DfUiModule,
        DfModuleImageModule,
        NgClass,
        ShellComponent,
        ImageModule,
        PhotoswipeDirective,
        VimeoPlayerComponent,
        ShopThisLookComponent,
        SwiperModule,
        SwiperDotsComponent,
        PhotoswipeLightboxComponent,
        ImageModule,
        FeaturesSectionComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductGalleryComponent implements OnInit, OnChanges {
    @Input()
    product!: LocalProduct<IProductDataV3>;
    media: Nexus.Lib.Catalog.Type.IProductMediaItem[] = [];
    mediaRows: IMediaRowItem[][] = [];
    mediaMobile: IMediaRowItem[] = [];
    swiper!: Swiper;
    photoswipeOptions: Partial<PhotoswipeUiDefaultOptions> = {};

    private gtmService = inject(LocalGtmService);
    private domSanitizer = inject(DomSanitizer);
    private viewer3dService = inject(Viewer3dService);

    constructor(
        private elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
        private timeoutService: TimeoutService,
        private client: Client,
        private renderer2: Renderer2
    ) {}

    ngOnInit(): void {
        this.photoswipeOptions = {
            showAnimationDuration: 250,
            hideAnimationDuration: 250,
            pinchToClose: false,
            closeOnScroll: false,
            closeOnVerticalDrag: false,
            tapToToggleControls: false,
            hiddenControlsMobile: false,
            counterEl: false,
            barsSize: { top: 0, bottom: 'auto' },
            initialZoom: 1,
            followMouse: true,
            canPreload: false,
            timeToIdle: 0,
            getDoubleTapZoom: (_isMouseClick: boolean, item: any) => {
                if (!item.zoomLevel) {
                    item.zoomLevel = item.initialZoomLevel;
                }
                let res: number;
                if (item.zoomLevel < 0.5 && (this.client.isM || this.client.isS)) {
                    res = 0.5;
                    this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-100');
                } else if (item.zoomLevel < 1) {
                    res = 1;
                    this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--zoom-100');
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-50');
                } else {
                    res = item.initialZoomLevel;
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--zoom-100');
                }
                item.zoomLevel = res;
                return res;
            }
        };
        this.onProductChange();
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['product']) {
            this.onProductChange();
        }
    }
    onModelChange() {
        this.onProductChange();
    }
    onAfterInit(eventParams: Parameters<SwiperEvents['afterInit']>) {
        const [swiper] = eventParams;
        this.swiper = swiper;
        this.detectChanges();
    }
    onPhotoswipeStateChange(state: boolean) {
        state
            ? this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--active')
            : this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--active');
        this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--inactive');
        this.timeoutService.setTimeout(() => {
            this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--inactive');
        }, 500);
    }
    onPhotoswipeChanged(index: number) {
        this.swiper?.slideTo(index);
    }
    private onProductChange() {
        this.media = this.getMedia() || [];
        this.mediaRows = this.getMediaRows() || [];
        this.mediaMobile = this.getMediaMobile() || [];
        // reset slides if swiper exists
        this.swiper?.setProgress(0);
        this.detectChanges();
    }
    ///////////////// MEDIA //////////////
    mediaRowTrackFn(_index: number, items: IMediaRowItem[]) {
        return items.map(i => i.uid).join('.');
    }
    mediaRowItemTrackFn(_index: number, item: IMediaRowItem) {
        return item.uid;
    }
    /**
     * Retrieves allowed media for gallery
     */
    private getMedia() {
        return filterMediaByLabel(this.product.media || [], productHighlightsLabel, true);
    }
    protected getMediaRows(): IMediaRowItem[][] {
        const rows: IMediaRowItem[][] = [];
        const media = this.getMedia();
        if (!media) return [];
        let skipItem: boolean = false;
        media.forEach((item, index) => {
            if (skipItem) {
                skipItem = false;
                return;
            }
            if (item.labels?.includes('large portrait')) {
                rows.push(this.getMediaRow([item], EMediaRowLayout.portrait));
            }
            if (item.labels?.includes('landscape')) {
                rows.push(this.getMediaRow([item], EMediaRowLayout.landscape));
            }
            if (item.labels?.includes('small portrait') || item.labels?.includes('portrait')) {
                const secondaryItem = media[index + 1];
                if (secondaryItem?.labels?.includes('small portrait') || secondaryItem?.labels?.includes('portrait')) {
                    rows.push(this.getMediaRow([item, secondaryItem], EMediaRowLayout.portraitsPair));
                    skipItem = true;
                } else {
                    rows.push(this.getMediaRow([item], EMediaRowLayout.portrait));
                }
            }
        });
        return this.product.isSoldOut ? rows.slice(0, 1) : rows;
    }

    private getMediaRow(items: Nexus.Lib.Catalog.Type.IProductMediaItem[], layout: EMediaRowLayout) {
        return items.map((item, index): IMediaRowItem => {
            const iframeSrc = this.viewer3dService.get3DViewerIframeSrc(item, this.product);
            return {
                ...item,
                ratio:
                    layout === EMediaRowLayout.landscape
                        ? 9 / 16
                        : layout === EMediaRowLayout.portrait
                          ? 4 / 3
                          : items.length === 1
                            ? 5 / 4
                            : 4 / 3,
                classes: [
                    layout === EMediaRowLayout.landscape
                        ? 'ratio-16-9 flex-span-1 landscape'
                        : layout === EMediaRowLayout.portrait
                          ? 'ratio-3-4 flex-span-1'
                          : items.length === 1
                            ? 'w-6 ratio-4-5'
                            : 'flex-span-1 ratio-3-4',
                    index === items.length - 1 ? '' : 'm-r'
                ].join(' '),
                shopTheLook: item.image_title?.split(','),
                uid: [layout, items.map(i => i.image)].join(),
                isLandscape: layout === EMediaRowLayout.landscape,
                iframeSrc,
                iframeSrcSafe: iframeSrc ? this.domSanitizer.bypassSecurityTrustResourceUrl(iframeSrc) : undefined
            };
        });
    }

    protected getIframeHtml(item: IMediaRowItem): string | undefined {
        if (item.iframeSrc) {
            return `<div  style="bottom:100px; left:0; right:0; top:0; position:absolute;" class="flex flex-justify-center"><div class="height-100 w-12 pos-relative" >
            <iframe class="fill" src=" ${item.iframeSrc}"></iframe></div></div>`;
        }
        return undefined;
    }

    protected getMediaMobile(): IMediaRowItem[] {
        if (this.mediaRows) {
            const mediaMobile: IMediaRowItem[] = ([] as IMediaRowItem[]).concat(...this.mediaRows);
            const baseIndex = mediaMobile.findIndex(el => el.labels?.includes('base'));
            if (baseIndex !== -1) {
                const baseItem = mediaMobile.splice(baseIndex, 1)[0];
                mediaMobile.unshift(baseItem);
            }
            return compact(mediaMobile);
        }
        return [];
    }
    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Handle 3D viewer iframe click event
     */
    protected handle3DViewerFocus(item: IMediaRowItem): void {
        this.viewer3dService.dispatch3DViewerFocus(this.product, item.iframeSrc);
    }
}
